import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Briefcase,
  Check,
  Gift,
  HandCoins,
  MoreVertical,
  RefreshCw,
  Sparkles,
  UserPlus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ApiError } from '../../../lib/api'
import type {
  ExchangePackRecord,
  ProgramMutationPayload,
  ProgramRecord,
} from '../../../types/programs'
import { useIacrmServices, useSyncIacrmServices } from '../../iacrm/hooks'
import { toast } from 'sonner'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Field, FieldLabel } from '@/components/ui/field'
import { IconTile } from '@/components/ui/icon-tile'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent } from '@/components/ui/card'
import { EntityCardIdentity } from '@/components/app/EntityCardIdentity'
import { programStatusBadgeClass } from '@/features/dashboard/utils/semanticBadges'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface ProgramFormDialogProps {
  open: boolean
  title: string
  submitLabel: string
  packs: ExchangePackRecord[]
  initialProgram?: ProgramRecord | null
  isSubmitting: boolean
  error: ApiError | null
  onClose: () => void
  onSubmit: (payload: ProgramMutationPayload) => Promise<void>
}

type FormState = {
  name: string
  description: string
  commission_type: ProgramMutationPayload['commission_type']
  exchange_mode: ProgramMutationPayload['exchange_mode']
  points_per_transaction: string
  points_per_euro: string
  exchange_pack_id: string
  eligibility_criteria: string
  status: NonNullable<ProgramMutationPayload['status']>
}

type StepErrors = Partial<Record<keyof FormState, string>>

type ExchangeModeOption = {
  value: NonNullable<FormState['exchange_mode']>
  label: string
  sublabel: string
  icon: typeof Briefcase
  tileClass: string
  activeBorder: string
}

function buildFormState(packs: ExchangePackRecord[], initial?: ProgramRecord | null): FormState {
  return {
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    commission_type: initial?.commission_type ?? 'per-transaction',
    exchange_mode: initial?.exchange_mode ?? 'both',
    points_per_transaction: initial?.points_per_transaction?.toString() ?? '',
    points_per_euro: initial?.points_per_euro?.toString() ?? '',
    exchange_pack_id: initial?.exchange_pack?.id ?? firstAssignablePackId(packs),
    eligibility_criteria: initial?.eligibility_criteria ?? '',
    status: initial?.status ?? 'active',
  }
}

function fieldApiError(error: ApiError | null, field: string) {
  return error?.errors?.[field]?.[0] ?? null
}

function normalizeIacrmText(value: string | null | undefined) {
  return (value ?? '').trim().toLowerCase()
}

function activeRewardItemCount(pack: ExchangePackRecord | null | undefined) {
  if (!pack) return 0
  return pack.active_items_count ?? pack.items.filter((i) => i.status === 'active').length
}

function isRewardPackAssignable(pack: ExchangePackRecord | null | undefined) {
  return activeRewardItemCount(pack) > 0
}

function firstAssignablePackId(packs: ExchangePackRecord[]) {
  return packs.find(isRewardPackAssignable)?.id ?? ''
}

function Textarea({ className, ...props }: React.ComponentProps<'textarea'>) {
  return (
    <textarea
      className={cn(
        'flex min-h-[80px] w-full rounded-[var(--radius)] border border-input bg-background px-3 py-2 text-sm text-foreground shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 dark:hover:bg-input/50',
        className,
      )}
      {...props}
    />
  )
}

function FieldError({ children }: { children: string | null | undefined }) {
  if (!children) return null
  return <p className="text-xs text-destructive">{children}</p>
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
      {children}
    </p>
  )
}

// ── Step indicator ──────────────────────────────────────────────────────────

function StepIndicator({ current, labels }: { current: number; labels: string[] }) {
  return (
    <div className="flex items-start justify-center gap-0 px-2">
      {labels.map((label, index) => {
        const step = index + 1
        const isDone = current > step
        const isActive = current === step

        return (
          <div key={label} className="flex items-start">
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={cn(
                  'flex size-6 items-center justify-center rounded-full text-[11px] font-semibold transition-all duration-200',
                  isDone
                    ? 'bg-foreground text-background'
                    : isActive
                      ? 'border-[1.5px] border-foreground bg-foreground/8 text-foreground'
                      : 'border-[1.5px] border-border bg-transparent text-muted-foreground',
                )}
              >
                {isDone ? <Check className="size-3" /> : step}
              </div>
              <span
                className={cn(
                  'text-[10px] font-medium whitespace-nowrap',
                  isActive ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {label}
              </span>
            </div>
            {index < labels.length - 1 && (
              <div
                className={cn(
                  'mx-3 mt-3 h-px w-10 shrink-0 transition-colors duration-200',
                  isDone ? 'bg-foreground' : 'bg-border',
                )}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Program card preview ────────────────────────────────────────────────────

function exchangeModeConfig(mode: FormState['exchange_mode'], t: (k: string) => string) {
  if (mode === 'cash') return { icon: HandCoins, tileClass: 'bg-emerald-500 text-white', label: t('programs.card.exchangeMode.cash'), metaBorder: 'border-emerald-500/35 bg-emerald-500/8', metaTitle: 'text-emerald-800 dark:text-emerald-300' }
  if (mode === 'reward') return { icon: Gift, tileClass: 'bg-amber-500 text-white', label: t('programs.card.exchangeMode.reward'), metaBorder: 'border-amber-500/35 bg-amber-500/8', metaTitle: 'text-amber-800 dark:text-amber-300' }
  return { icon: Briefcase, tileClass: 'bg-blue-500 text-white', label: t('programs.card.exchangeMode.both'), metaBorder: 'border-blue-500/25 bg-blue-500/8', metaTitle: 'text-blue-800 dark:text-blue-300' }
}

function ProgramCardPreview({
  form,
  selectedPack,
  locale,
}: {
  form: FormState
  selectedPack: ExchangePackRecord | null
  locale: string
}) {
  const { t } = useTranslation()
  const modeConfig = exchangeModeConfig(form.exchange_mode, t)
  const hasCash = form.exchange_mode === 'cash' || form.exchange_mode === 'both'
  const hasReward = form.exchange_mode === 'reward' || form.exchange_mode === 'both'

  const pointsSummary = form.commission_type === 'per-transaction' && form.points_per_transaction
    ? t('programs.card.roleSummary.perTransactionValue', { count: Number(form.points_per_transaction).toLocaleString(locale) })
    : form.commission_type === 'per-transaction'
      ? t('programs.card.roleSummary.perTransactionFallback')
      : t('programs.card.roleSummary.revenueTier')

  const statusBadgeClass = programStatusBadgeClass(form.status as never)
  const statusLabel: Record<FormState['status'], string> = {
    active: t('programs.status.active'),
    draft: t('programs.status.draft'),
    paused: t('programs.status.paused'),
    suspended: t('programs.status.suspended'),
    archived: t('programs.status.archived'),
  }

  return (
    <Card className="border-border shadow-sm w-full">
      {/* Card header */}
      <div className="flex items-start justify-between gap-2 px-3 pt-3 pb-1 sm:px-4">
        <EntityCardIdentity
          leading={<IconTile icon={modeConfig.icon} size="md" className={modeConfig.tileClass} />}
          title={form.name || <span className="text-muted-foreground italic">{t('programs.form.placeholders.name')}</span>}
          description={form.description || t('programs.card.noDescription')}
          badge={
            <Badge variant="secondary" className={cn('border-0 text-[11px]', statusBadgeClass)}>
              {statusLabel[form.status]}
            </Badge>
          }
          className="flex-1 min-w-0"
          titleClassName="!text-sm text-foreground"
          descriptionClassName="text-xs"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground opacity-40 cursor-default"
          tabIndex={-1}
        >
          <MoreVertical className="size-4" />
        </Button>
      </div>

      <CardContent className="space-y-2 px-3 pb-3 pt-1 sm:px-4">
        {/* Meta grid */}
        <div className="grid gap-2 sm:grid-cols-2">
          {/* Attribution / commission */}
          <div className="rounded-lg border border-dashed border-border/60 bg-muted/10 px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {t('programs.card.meta.attribution')}
            </p>
            <p className="mt-1 text-xs font-semibold text-foreground">{pointsSummary}</p>
          </div>
          {/* Exchange mode */}
          <div className="rounded-lg border border-dashed border-border/60 bg-muted/10 px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {t('programs.card.meta.points')}
            </p>
            <p className="mt-1 text-xs font-medium text-foreground">{modeConfig.label}</p>
          </div>
        </div>

        {/* Cash / reward detail */}
        {(hasCash || hasReward) && (
          <div className="grid gap-2">
            {hasCash && (
              <div className={cn('rounded-lg border px-3 py-2', 'border-emerald-500/35 bg-emerald-500/8')}>
                <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-800 dark:text-emerald-300">
                  {t('programs.card.meta.cash')}
                </p>
                <p className="mt-1 text-xs font-semibold text-foreground">
                  {form.points_per_euro
                    ? <><strong>{form.points_per_euro}</strong> {t('common.pts')} = 1 EUR</>
                    : <span className="text-muted-foreground">{t('programs.dialogs.currentConfigEmpty')}</span>}
                </p>
              </div>
            )}
            {hasReward && (
              <div className={cn('rounded-lg border px-3 py-2', 'border-amber-500/35 bg-amber-500/8')}>
                <p className="text-[10px] uppercase tracking-[0.16em] text-amber-800 dark:text-amber-300">
                  {selectedPack ? t('programs.card.meta.rewards', { name: selectedPack.name }) : t('programs.card.meta.rewards', { name: t('programs.card.meta.defaultPack') })}
                </p>
                {selectedPack?.items.length ? (
                  <ul className="mt-1 space-y-0.5">
                    {selectedPack.items.filter(i => i.status === 'active').slice(0, 3).map(item => (
                      <li key={item.id} className="text-xs text-foreground">
                        {t('programs.card.rewards.itemCost', { title: item.title, count: item.points_cost.toLocaleString(locale) })}
                      </li>
                    ))}
                    {selectedPack.items.filter(i => i.status === 'active').length > 3 && (
                      <li className="text-xs text-muted-foreground">+{selectedPack.items.filter(i => i.status === 'active').length - 3}</li>
                    )}
                  </ul>
                ) : (
                  <p className="mt-1 text-xs text-amber-900 dark:text-amber-300">{t('programs.card.rewards.noItems')}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Agent assignments area */}
        <div className="rounded-lg border border-dashed border-border/60 bg-muted/10 px-3 py-2">
          <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            {t('programs.card.assignments.summary', { count: 0 })}
          </p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-border/60 bg-muted/30">
              <UserPlus className="h-3 w-3 text-muted-foreground/50" />
            </div>
            <p className="text-xs text-muted-foreground">{t('programs.card.assignments.none')}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Main component ──────────────────────────────────────────────────────────

export function ProgramFormDialog({
  open,
  title,
  submitLabel,
  packs,
  initialProgram,
  isSubmitting,
  error,
  onClose,
  onSubmit,
}: ProgramFormDialogProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US'
  const [step, setStep] = useState(1)
  const [form, setForm] = useState<FormState>(() => buildFormState(packs, initialProgram))
  const [stepErrors, setStepErrors] = useState<StepErrors>({})
  const [selectedServiceId, setSelectedServiceId] = useState('')

  const exchangeModes: ExchangeModeOption[] = useMemo(
    () => [
      {
        value: 'both',
        label: t('programs.exchangeModes.both'),
        sublabel: t('programs.form.exchangeModeSublabels.both'),
        icon: Briefcase,
        tileClass: 'bg-blue-500 text-white',
        activeBorder: 'border-blue-400 bg-blue-50/60 dark:bg-blue-500/8',
      },
      {
        value: 'reward',
        label: t('programs.exchangeModes.reward'),
        sublabel: t('programs.form.exchangeModeSublabels.reward'),
        icon: Gift,
        tileClass: 'bg-amber-500 text-white',
        activeBorder: 'border-amber-400 bg-amber-50/60 dark:bg-amber-500/8',
      },
      {
        value: 'cash',
        label: t('programs.exchangeModes.cash'),
        sublabel: t('programs.form.exchangeModeSublabels.cash'),
        icon: HandCoins,
        tileClass: 'bg-emerald-500 text-white',
        activeBorder: 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-500/8',
      },
    ],
    [t],
  )

  const stepLabels = useMemo(
    () => [
      t('programs.form.steps.info'),
      t('programs.form.steps.commission'),
      t('programs.form.steps.preview'),
    ],
    [t],
  )

  const iacrmServicesQuery = useIacrmServices()
  const syncServicesMutation = useSyncIacrmServices()
  const activeIacrmServices = useMemo(
    () => (iacrmServicesQuery.data?.data ?? []).filter((s) => s.is_active),
    [iacrmServicesQuery.data],
  )

  function patchForm(next: Partial<FormState>) {
    setForm((cur) => ({ ...cur, ...next }))
  }

  function clearError(field: keyof FormState) {
    setStepErrors((cur) => {
      if (!cur[field]) return cur
      const next = { ...cur }
      delete next[field]
      return next
    })
  }

  useEffect(() => {
    if (!open) return
    setStep(1)
    setForm(buildFormState(packs, initialProgram))
    setStepErrors({})
    setSelectedServiceId('')
  }, [open, packs, initialProgram])

  useEffect(() => {
    if (!open || selectedServiceId || !initialProgram || !activeIacrmServices.length) return
    const matchedService = activeIacrmServices.find((service) => {
      const sameName = normalizeIacrmText(service.name) === normalizeIacrmText(initialProgram.name)
      const pd = normalizeIacrmText(initialProgram.description)
      const sd = normalizeIacrmText(service.description)
      return sameName && (!pd || !sd || sd === pd)
    })
    if (matchedService) setSelectedServiceId(matchedService.iacrm_id)
  }, [activeIacrmServices, initialProgram, open, selectedServiceId])

  const selectedPack = useMemo(
    () => packs.find((p) => p.id === form.exchange_pack_id) ?? null,
    [form.exchange_pack_id, packs],
  )
  const hasCash = form.exchange_mode === 'cash' || form.exchange_mode === 'both'
  const hasReward = form.exchange_mode === 'reward' || form.exchange_mode === 'both'

  function validateStep1(): StepErrors {
    const errs: StepErrors = {}
    if (!form.name.trim()) errs.name = t('programs.form.errors.nameRequired')
    if (!form.description.trim()) errs.description = t('programs.form.errors.descriptionRequired')
    if (!form.eligibility_criteria.trim()) errs.eligibility_criteria = t('programs.form.errors.eligibilityRequired')
    return errs
  }

  function validateStep2(): StepErrors {
    const errs: StepErrors = {}
    if (form.commission_type === 'per-transaction' && !form.points_per_transaction.trim())
      errs.points_per_transaction = t('programs.form.errors.pointsRequired')
    if (hasCash && !form.points_per_euro.trim())
      errs.points_per_euro = t('programs.form.errors.cashRequired')
    if (hasReward && !form.exchange_pack_id)
      errs.exchange_pack_id = t('programs.form.errors.rewardPackRequired')
    if (hasReward && form.exchange_pack_id && !isRewardPackAssignable(selectedPack))
      errs.exchange_pack_id = t('programs.dialogs.rewardPackEmptyActiveItems')
    return errs
  }

  function handleNext() {
    const errs = step === 1 ? validateStep1() : step === 2 ? validateStep2() : {}
    if (Object.keys(errs).length > 0) { setStepErrors(errs); return }
    setStepErrors({})
    setStep((c) => c + 1)
  }

  async function handleSubmit() {
    await onSubmit({
      name: form.name.trim(),
      description: form.description.trim(),
      commission_type: form.commission_type,
      exchange_mode: form.exchange_mode,
      points_per_transaction: form.points_per_transaction.trim() ? Number(form.points_per_transaction) : null,
      points_per_euro: form.points_per_euro.trim() ? Number(form.points_per_euro) : null,
      exchange_pack_id: hasReward ? form.exchange_pack_id || null : null,
      eligibility_criteria: form.eligibility_criteria.trim(),
      status: form.status,
    })
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="sm:max-w-xl max-h-[92vh] flex flex-col gap-0 p-0 overflow-hidden" showCloseButton>

        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-4 shrink-0">
          <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('programs.form.dialogDescription', { defaultValue: 'Créez ou modifiez un programme de parrainage.' })}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="px-6 pb-4 shrink-0">
          <StepIndicator current={step} labels={stepLabels} />
        </div>

        <Separator />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 min-h-0">

          {/* ── Step 1: Informations ── */}
          {step === 1 && (
            <>
              {/* IACRM prefill */}
              <div className="relative rounded-[var(--radius)] border border-amber-200/80 bg-amber-50/50 p-4 dark:border-amber-800/40 dark:bg-amber-950/20">
                <div className="absolute left-0 top-3 bottom-3 w-0.5 rounded-full bg-amber-400" />
                <div className="pl-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="size-3 text-amber-600 dark:text-amber-400 shrink-0" />
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-400">
                        {t('programs.form.prefill.title')}
                      </p>
                    </div>
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-7 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30"
                            onClick={() => syncServicesMutation.mutate(undefined, {
                              onSuccess: (data) => {
                                const r = data.result
                                toast.success(`${r.created} créé(s), ${r.updated} mis à jour`)
                              },
                              onError: () => toast.error('Échec de la synchronisation'),
                            })}
                            disabled={syncServicesMutation.isPending}
                          >
                            <RefreshCw className={cn('size-3.5', syncServicesMutation.isPending && 'animate-spin')} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">{t('programs.form.prefill.syncTooltip')}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-xs text-amber-700/70 dark:text-amber-400/70 mb-3">
                    {t('programs.form.prefill.description')}
                  </p>

                  {iacrmServicesQuery.isPending ? (
                    <p className="text-xs text-muted-foreground">
                      Chargement des services et produits IACRM...
                    </p>
                  ) : activeIacrmServices.length === 0 ? (
                    <p className="rounded-[var(--radius)] border border-dashed border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                      Aucun service ou produit trouvé dans IACRM.
                    </p>
                  ) : null}

                  {activeIacrmServices.length > 0 && (
                    <Select
                      value={selectedServiceId}
                      onValueChange={(id) => {
                        setSelectedServiceId(id)
                        const service = activeIacrmServices.find((e) => e.iacrm_id === id)
                        if (service) {
                          patchForm({ name: service.name, description: service.description ?? '' })
                          clearError('name')
                          clearError('description')
                        }
                      }}
                      disabled={iacrmServicesQuery.isPending}
                    >
                      <SelectTrigger className="w-full bg-background">
                        <SelectValue placeholder={t('programs.form.prefill.placeholder')} />
                      </SelectTrigger>
                      <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                        {activeIacrmServices.map((service) => {
                          const priceStr = service.unit_price
                            ? ` · ${service.unit_price.toLocaleString(locale, { style: 'currency', currency: 'EUR' })}`
                            : ''
                          const catStr = service.category ? ` · ${service.category}` : ''
                          const label = `${service.name}${priceStr}${catStr}`
                          return (
                            <SelectItem key={service.iacrm_id} value={service.iacrm_id} title={label}>
                              <span className="block truncate min-w-0">{label}</span>
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {/* Name + Status */}
              <div className="grid gap-4 sm:grid-cols-2">
                <Field>
                  <FieldLabel htmlFor="prog-name">{t('programs.form.fields.name')}</FieldLabel>
                  <Input
                    id="prog-name"
                    value={form.name}
                    onChange={(e) => { patchForm({ name: e.target.value }); clearError('name') }}
                    placeholder={t('programs.form.placeholders.name')}
                  />
                  <FieldError>{stepErrors.name ?? fieldApiError(error, 'name')}</FieldError>
                </Field>

                <Field>
                  <FieldLabel htmlFor="prog-status">{t('programs.form.fields.initialStatus')}</FieldLabel>
                  <Select value={form.status} onValueChange={(v) => patchForm({ status: v as FormState['status'] })}>
                    <SelectTrigger id="prog-status" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">{t('programs.status.draft')}</SelectItem>
                      <SelectItem value="active">{t('programs.status.active')}</SelectItem>
                      <SelectItem value="paused">{t('programs.status.paused')}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field>
                <FieldLabel htmlFor="prog-desc">{t('programs.form.fields.description')}</FieldLabel>
                <Textarea
                  id="prog-desc"
                  value={form.description}
                  onChange={(e) => { patchForm({ description: e.target.value }); clearError('description') }}
                  placeholder={t('programs.form.placeholders.description')}
                  rows={3}
                />
                <FieldError>{stepErrors.description ?? fieldApiError(error, 'description')}</FieldError>
              </Field>

              <Field>
                <FieldLabel htmlFor="prog-eligibility">{t('programs.form.fields.eligibilityCriteria')}</FieldLabel>
                <Textarea
                  id="prog-eligibility"
                  value={form.eligibility_criteria}
                  onChange={(e) => { patchForm({ eligibility_criteria: e.target.value }); clearError('eligibility_criteria') }}
                  placeholder={t('programs.form.placeholders.eligibilityCriteria')}
                  rows={2}
                />
                <FieldError>{stepErrors.eligibility_criteria ?? fieldApiError(error, 'eligibility_criteria')}</FieldError>
              </Field>
            </>
          )}

          {/* ── Step 2: Commission ── */}
          {step === 2 && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field>
                  <FieldLabel htmlFor="prog-commission-type">{t('programs.form.fields.commissionType')}</FieldLabel>
                  <Select
                    value={form.commission_type}
                    onValueChange={(v) => {
                      patchForm({ commission_type: v as FormState['commission_type'] })
                      clearError('commission_type')
                      clearError('points_per_transaction')
                    }}
                  >
                    <SelectTrigger id="prog-commission-type" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per-transaction">{t('programs.form.commissionType.perTransaction')}</SelectItem>
                      <SelectItem value="revenue-tier">{t('programs.form.commissionType.revenueTier')}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>

                <Field>
                  <FieldLabel htmlFor="prog-points-per-tx">{t('programs.form.fields.pointsPerTransaction')}</FieldLabel>
                  <Input
                    id="prog-points-per-tx"
                    type="number"
                    min="1"
                    max="9999999"
                    value={form.points_per_transaction}
                    onChange={(e) => { patchForm({ points_per_transaction: e.target.value }); clearError('points_per_transaction') }}
                    placeholder={t('programs.form.placeholders.pointsPerTransaction')}
                    disabled={form.commission_type !== 'per-transaction'}
                  />
                  <FieldError>{stepErrors.points_per_transaction ?? fieldApiError(error, 'points_per_transaction')}</FieldError>
                  {!stepErrors.points_per_transaction && form.commission_type !== 'per-transaction' && (
                    <p className="text-xs text-muted-foreground">{t('programs.form.help.revenueTierNoPoints')}</p>
                  )}
                </Field>
              </div>

              {/* Exchange mode */}
              <div>
                <SectionLabel>{t('programs.form.fields.exchangeMode')}</SectionLabel>
                <div className="grid gap-2 sm:grid-cols-3">
                  {exchangeModes.map((mode) => {
                    const isActive = form.exchange_mode === mode.value
                    return (
                      <button
                        key={mode.value}
                        type="button"
                        onClick={() => {
                          patchForm({
                            exchange_mode: mode.value,
                            exchange_pack_id:
                              mode.value === 'cash' ? '' : form.exchange_pack_id || firstAssignablePackId(packs),
                          })
                          clearError('exchange_mode')
                          clearError('points_per_euro')
                          clearError('exchange_pack_id')
                        }}
                        className={cn(
                          'flex items-center gap-3 rounded-[var(--radius)] border px-3 py-2.5 text-left transition-all duration-150',
                          isActive
                            ? cn('border', mode.activeBorder)
                            : 'border-border bg-background hover:bg-muted/40',
                        )}
                      >
                        <IconTile icon={mode.icon} className={mode.tileClass} size="sm" />
                        <div className="min-w-0">
                          <p className={cn('text-xs font-semibold truncate', isActive ? 'text-foreground' : 'text-foreground/80')}>
                            {mode.label}
                          </p>
                          <p className="mt-0.5 text-[10px] leading-4 text-muted-foreground line-clamp-2">
                            {mode.sublabel}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Cash section */}
              {hasCash && (
                <div className="rounded-[var(--radius)] border border-emerald-200/70 bg-emerald-50/40 p-4 dark:border-emerald-800/40 dark:bg-emerald-950/20 space-y-3">
                  <div className="flex items-center gap-2">
                    <IconTile icon={HandCoins} className="bg-emerald-500 text-white" size="sm" />
                    <SectionLabel>{t('programs.form.sections.cash')}</SectionLabel>
                  </div>
                  <Field>
                    <FieldLabel htmlFor="prog-points-per-eur">{t('programs.form.fields.cashPointsPerEuro')}</FieldLabel>
                    <Input
                      id="prog-points-per-eur"
                      type="number"
                      min="1"
                      max="9999999"
                      value={form.points_per_euro}
                      onChange={(e) => { patchForm({ points_per_euro: e.target.value }); clearError('points_per_euro') }}
                      placeholder={t('programs.form.placeholders.pointsPerEuro')}
                      className="bg-background"
                    />
                    <FieldError>{stepErrors.points_per_euro ?? fieldApiError(error, 'points_per_euro')}</FieldError>
                    {!stepErrors.points_per_euro && form.points_per_euro && (
                      <p className="text-xs text-emerald-700 dark:text-emerald-400">
                        {t('programs.card.cash.configured', { count: form.points_per_euro })}
                      </p>
                    )}
                    {!stepErrors.points_per_euro && !form.points_per_euro && (
                      <p className="text-xs text-muted-foreground">{t('programs.form.help.cashRateEmpty')}</p>
                    )}
                  </Field>
                </div>
              )}

              {/* Reward pack section */}
              {hasReward && (
                <div className="rounded-[var(--radius)] border border-amber-200/70 bg-amber-50/40 p-4 dark:border-amber-800/40 dark:bg-amber-950/20 space-y-3">
                  <div className="flex items-center gap-2">
                    <IconTile icon={Gift} className="bg-amber-500 text-white" size="sm" />
                    <SectionLabel>{t('programs.form.sections.rewards')}</SectionLabel>
                  </div>
                  <Field>
                    <FieldLabel htmlFor="prog-pack">{t('programs.form.fields.rewardPack')}</FieldLabel>
                    <Select
                      value={form.exchange_pack_id}
                      onValueChange={(v) => { patchForm({ exchange_pack_id: v }); clearError('exchange_pack_id') }}
                    >
                      <SelectTrigger id="prog-pack" className="w-full bg-background">
                        <SelectValue placeholder={t('programs.form.placeholders.rewardPack')} />
                      </SelectTrigger>
                      <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                        {packs.map((pack) => (
                          <SelectItem key={pack.id} value={pack.id} disabled={!isRewardPackAssignable(pack)} title={pack.name}>
                            <span className="block truncate min-w-0">
                              {isRewardPackAssignable(pack)
                                ? pack.name
                                : t('programs.dialogs.rewardPackInactiveLabel', { name: pack.name })}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FieldError>{stepErrors.exchange_pack_id ?? fieldApiError(error, 'exchange_pack_id')}</FieldError>
                  </Field>
                </div>
              )}
            </>
          )}

          {/* ── Step 3: Preview ── */}
          {step === 3 && (
            <>
              <p className="text-xs text-muted-foreground">
                {t('programs.form.stepDescriptions.preview')}
              </p>

              <ProgramCardPreview form={form} selectedPack={selectedPack} locale={locale} />

              {error && !error.errors && (
                <div className="rounded-[var(--radius)] border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                  {error.message}
                </div>
              )}
            </>
          )}
        </div>

        <Separator />

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => { if (step === 1) onClose(); else setStep((c) => c - 1) }}
            disabled={isSubmitting}
            className="text-muted-foreground"
          >
            {step === 1 ? t('common.cancel') : t('common.back')}
          </Button>

          {step < 3 ? (
            <Button type="button" size="sm" onClick={handleNext}>
              {t('common.next')}
            </Button>
          ) : (
            <Button type="button" size="sm" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? t('programs.dialogs.saving') : submitLabel}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
